import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Phone, ShieldCheck, ArrowRight, KeyRound, User, Heart, Building2, ShieldAlert } from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in — Mobile OTP Login · Daan Amrut Foundation" },
      { name: "description", content: "Sign in with your mobile number and OTP to track donations, download 80G receipts and manage your seva." },
    ],
  }),
  component: Login,
});

function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [busy, setBusy] = useState(false);

  function handlePhoneSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!phone || phone.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setBusy(true);
    setTimeout(() => {
      setBusy(false);
      setStep("otp");
      toast.success("OTP sent to +91 " + phone);
    }, 600);
  }

  function handleOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length < 6) {
      toast.error("Please enter the complete 6-digit OTP code");
      return;
    }
    setBusy(true);
    setTimeout(() => {
      setBusy(false);
      toast.success("Mobile verified! Welcome to Daan Amrut.");
      navigate({ to: "/donor" });
    }, 800);
  }

  function autoFillDemoOtp() {
    setOtp(["1", "2", "3", "4", "5", "6"]);
    toast.info("Demo OTP 123456 applied");
  }

  return (
    <SiteShell>
      <section className="mx-auto grid max-w-5xl gap-10 px-4 py-16 sm:px-6 md:grid-cols-2 items-center">
        {/* Left Side: Brand Value Proposition */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-[var(--saffron)]/15 px-3 py-1 text-xs font-semibold text-[var(--saffron-deep)]">
            <ShieldCheck className="h-3.5 w-3.5" /> Instant Mobile + OTP Login
          </div>
          <h1 className="font-display text-3xl font-bold text-[var(--maroon-deep)] sm:text-4xl leading-tight">
            Transparent Seva starts with a single tap.
          </h1>
          <p className="text-sm text-foreground/75 leading-relaxed">
            Sign in with your mobile number to view past donations, download 80G tax exemption receipts, track fodder dispatches, and manage monthly recurring seva.
          </p>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="rounded-xl border border-border/80 bg-muted/40 p-3.5">
              <p className="text-xs font-bold text-foreground">80G Receipts</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Instant PDF download for tax saving</p>
            </div>
            <div className="rounded-xl border border-border/80 bg-muted/40 p-3.5">
              <p className="text-xs font-bold text-foreground">Live Tracking</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Track fodder trucks to gaushalas</p>
            </div>
          </div>
        </div>

        {/* Right Side: Mobile OTP Form Card */}
        <div className="rounded-2xl border border-border/80 bg-card p-7 shadow-lg shadow-[var(--maroon)]/5">
          {step === "phone" ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-5">
              <div>
                <h2 className="font-display text-xl font-bold text-[var(--maroon-deep)]">Mobile Login</h2>
                <p className="text-xs text-muted-foreground mt-1">Enter your mobile number to receive a 6-digit OTP</p>
              </div>

              <div>
                <label className="text-xs font-medium text-foreground/80 mb-1.5 block">Mobile Number</label>
                <div className="flex items-center rounded-xl border border-border bg-background px-3 py-2.5 focus-within:ring-2 focus-within:ring-[var(--maroon)]">
                  <span className="text-xs font-bold text-muted-foreground border-r border-border pr-2.5 mr-2.5">+91</span>
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="98765 43210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    className="w-full bg-transparent text-sm font-semibold outline-none tracking-wide"
                    required
                  />
                  <Phone className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--maroon)] py-3 text-sm font-bold text-white shadow-md hover:bg-[var(--maroon-deep)] active:scale-98 transition"
              >
                {busy ? "Sending OTP…" : "Get OTP Code"} <ArrowRight className="h-4 w-4" />
              </button>

              <div className="relative border-t border-border/80 my-4 text-center">
                <span className="bg-card px-3 text-[11px] text-muted-foreground relative -top-2.5">OR Quick Switch Role</span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Link to="/donor" className="rounded-xl border border-border/80 p-2.5 text-center text-xs font-medium hover:bg-muted/50 transition">
                  <Heart className="h-4 w-4 text-[var(--maroon)] mx-auto mb-1" /> Donor
                </Link>
                <Link to="/institution" className="rounded-xl border border-border/80 p-2.5 text-center text-xs font-medium hover:bg-muted/50 transition">
                  <Building2 className="h-4 w-4 text-[var(--saffron-deep)] mx-auto mb-1" /> Gaushala
                </Link>
                <Link to="/admin" className="rounded-xl border border-border/80 p-2.5 text-center text-xs font-medium hover:bg-muted/50 transition">
                  <ShieldAlert className="h-4 w-4 text-foreground mx-auto mb-1" /> Admin
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-5">
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-xl font-bold text-[var(--maroon-deep)]">Enter OTP Code</h2>
                  <button
                    type="button"
                    onClick={() => setStep("phone")}
                    className="text-xs text-[var(--maroon)] font-medium underline"
                  >
                    Change Number
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Sent to +91 {phone}</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-medium text-foreground/80">6-Digit Verification Code</label>
                  <button
                    type="button"
                    onClick={autoFillDemoOtp}
                    className="text-xs font-bold text-[var(--saffron-deep)] hover:underline flex items-center gap-1"
                  >
                    <KeyRound className="h-3 w-3" /> Auto-fill Demo OTP (123456)
                  </button>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      id={`otp-${i}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => {
                        const val = e.target.value;
                        const newOtp = [...otp];
                        newOtp[i] = val;
                        setOtp(newOtp);
                        if (val && i < 5) {
                          document.getElementById(`otp-${i + 1}`)?.focus();
                        }
                      }}
                      className="h-12 w-full text-center font-display text-lg font-bold rounded-xl border border-border bg-background focus:ring-2 focus:ring-[var(--maroon)] outline-none"
                    />
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--maroon)] py-3 text-sm font-bold text-white shadow-md hover:bg-[var(--maroon-deep)] active:scale-98 transition"
              >
                {busy ? "Verifying…" : "Verify & Continue"}
              </button>
            </form>
          )}
        </div>
      </section>
    </SiteShell>
  );
}